import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import type { TagSlug } from 'interfaces/Taxonomy';
import type { Hub } from 'interfaces/Home';
import type { HeaderNav, MenuItemProps } from 'interfaces/navigation/Header';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import getTag from 'api/Tag/getTag';
import getAllTags from 'api/Tag/getAllTags';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import type { Head } from 'interfaces/Head';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import { setPianoConfig } from 'services/Piano';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import setSectionPageAdConfig from 'services/Ad/config/setSectionPageAdConfig';
import type { PageAdConfig } from 'interfaces/ads/Ad';
import { layoutCount, topicNavBorderColor } from 'constants/constants';
import MostPopularRR from 'components/MostPopularRR';
import HubPage from 'pages/Hub/HubPage';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { PageType } from 'interfaces/content/articles/Post';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import NotFoundError from 'interfaces/errors/NotFoundError';

interface TagProps {
  mostPopular: ArticlePreview[];
  tag: Hub;
  slug: string;
  subMenu?: MenuItemProps[];
  pageSize: number;
  pagination: PagePagination;
}

const TagNextPage: NextPage<TagProps> = ({ mostPopular, tag, slug, subMenu, pageSize, pagination }: TagProps) => (
  <HubPage
    data={tag}
    type={PageType.TAG}
    sidebar={[
      mostPopular && (
        <MostPopularRR
          key={1}
          articles={mostPopular}
        />
      ),
    ]}
    infiniteScroll={{ numPostsPerPage: layoutCount.tag, slug, taxonomy: 'tag' }}
    subMenu={subMenu}
    color={topicNavBorderColor.own}
    pageSize={pageSize}
    pagination={pagination}
  />
);
export default TagNextPage;

interface Props {
  params: {
    slug: string;
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.tag;

  try {
    const { slug } = context.params as { slug: string };
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);

    const tag = await getTag(slug, pageSize, (currentPageNum - 1) * pageSize, true);
    if (tag.disablePage) {
      throw new NotFoundError('Page not found');
    }
    const totalArticles = tag.pageInfo.totalCount > 0 ? tag.pageInfo.totalCount : 0;

    const pagination = calculatePagePagination(currentPageNum, totalArticles);

    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const mostPopular = await safeAsync(() => getMostPopularPosts(), fallbackData.articlesData.posts);

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(tag), fallbackData.dataLayer);
    const headData: HeadData = safe(
      () =>
        setHeadData(
          {
            ...tag,
            description: tag.socialDescription, // use socialDescription to have all meta descriptions be consistent
            canonicalUrl: pageNum ? `${tag.canonicalUrl}page/${pageNum}/` : tag.canonicalUrl,
            titlePrefix: pagination.pageInfo,
          } as Head,
          true,
          'Fortune',
        ),
      fallbackData.headData,
    );
    const arcSpanPageConfig: ArcSpanPageConfig = safe(() => setarcSpanPageConfig(tag), fallbackData.arcSpanPageConfig);
    const pianoPageConfig: PianoPageConfig = safe(() => setPianoConfig(tag), fallbackData.pianoPageConfig);
    const pageAdConfig: PageAdConfig = safe(() => setSectionPageAdConfig(tag), fallbackData.pageAdConfig);
    const subMenu = menu?.topicNavigation.find((menuItem) => menuItem.label === tag.name)?.subMenu || null;

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        mostPopular,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig,
        slug,
        subMenu,
        tag,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            slug: 'auto',
          },
        },
      ],
    };
  }

  const { tags }: { tags: TagSlug[] } = await getAllTags();
  const paths: Props[] = tags.map((tag) => ({
    params: {
      slug: tag.slug,
    },
  }));
  return {
    fallback: 'blocking',
    paths,
  };
};
